
import { supabase } from "@/integrations/supabase/client";
import { ScenarioType, AnalysisPromptData, AnalysisData } from "@/types/analysis";
import { CustomCanvas } from "@/types/fabric";
import { Canvas } from "fabric";

export const saveAnalysis = async (
  analysisText: string, 
  scenarioType: ScenarioType,
  currentDrawingId: string,
  userId: string,
  canvas: Canvas | null
) => {
  if (!currentDrawingId || !userId) {
    console.log('Skipping analysis save - no drawing ID or user ID');
    return;
  }

  console.log('Saving analysis for drawing:', currentDrawingId);

  try {
    // Update canvas data with both types for consistency
    if (canvas) {
      (canvas as CustomCanvas).data = {
        ...(canvas as CustomCanvas).data,
        analysisText,
        scenarioType,
        procedureType: scenarioType  // Keep both in sync
      };
    }

    // Create record in drawing_analyses
    const { error: analysisError } = await supabase
      .from('drawing_analyses')
      .insert({
        drawing_id: currentDrawingId,
        user_id: userId,
        analysis_text: analysisText,
        procedure_type: scenarioType,  // Use the original scenario type
        status: 'completed'
      });

    if (analysisError) throw analysisError;

    console.log('Analysis saved successfully');
  } catch (error) {
    console.error('Error saving analysis:', error);
    // Don't throw error since saving is optional
  }
};

export const loadExistingAnalysis = async (currentDrawingId: string, canvas: Canvas | null) => {
  try {
    const { data: analysisData, error: analysisError } = await supabase
      .from('drawing_analyses')
      .select('analysis_text, procedure_type')
      .eq('drawing_id', currentDrawingId)
      .eq('is_current', true)
      .maybeSingle();

    if (analysisError) throw analysisError;

    if (analysisData) {
      console.log('Loaded analysis:', analysisData);
      
      // Update canvas data with both types for consistency
      if (canvas && analysisData.procedure_type) {
        console.log('Setting scenario type from analysis:', analysisData.procedure_type);
        (canvas as CustomCanvas).data = {
          ...(canvas as CustomCanvas).data,
          analysisText: analysisData.analysis_text,
          scenarioType: analysisData.procedure_type as ScenarioType,
          procedureType: analysisData.procedure_type
        };
      }
    }

    return analysisData;
  } catch (error) {
    console.error('Error loading existing analysis:', error);
    throw error;
  }
};

export const getAnalysisPrompt = async (scenarioType: ScenarioType): Promise<AnalysisPromptData> => {
  console.log('Getting analysis prompt for scenario type:', scenarioType);
  
  const { data, error } = await supabase
    .from('analysis_prompts')
    .select('*')
    .eq('scenario_type', 'scenario') // Always use generic 'scenario' type
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch analysis prompt:', error);
    throw new Error('Failed to fetch analysis prompt');
  }
  if (!data) {
    console.error('No analysis prompt found for generic scenario type');
    throw new Error('No analysis prompt found for generic scenario type');
  }

  console.log('Found analysis prompt:', {
    version: data.version,
    scenario_type: data.scenario_type,
    is_active: data.is_active
  });

  return data;
};

export const analyzeDrawingWithAI = async (
  dataUrl: string,
  scenarioType: ScenarioType,
  promptVersion: number
) => {
  console.log('Running analysis for drawing with fresh context...');
  
  // Perform a single analysis with the current canvas data
  const { data, error } = await supabase.functions.invoke('analyze-drawing', {
    body: { 
      imageData: dataUrl,
      procedure: scenarioType,
      promptVersion,
      clearContext: true // Add a flag to ensure fresh analysis
    }
  });

  if (error) {
    console.error('Error from analyze-drawing function:', error);
    throw new Error('Analysis service unavailable');
  }
  
  return data;
};
